import React, { useState, useRef } from 'react';
import Feedback from '../feedback';
import { serverURL } from '../../config/urls';
import { logEvent } from '@firebase/analytics';
import ScreenWarning from '../screensize-warning';
import "../../styles/uiux-designer.css";
import Dropdown from '../dropdown';
import SpotTheDifference from './ui-game';
import { storage, analytics } from '../../config/firebase-logging';
import { v4 as uuidv4 } from 'uuid';
import {
    ref,
    uploadBytes,
    getDownloadURL,
  } from "firebase/storage";
  import { useCareerContext } from '../career-context';

export default function UIUXDesigner() {
    console.log('here')
    const {
        isPopupOpen,
        setIsPopupOpen,
        handleError,
        handleNotification,
        startConfetti
    } = useCareerContext();

    const contentRef = useRef(null);
    const [slide, setSlide] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [websiteIdea, setWebsiteIdea] = useState('');
    const [sampyThoughts, setSampyThoughts] = useState('');
    const [frqResults, setFrqResults] = useState({
        1: null,
        2: null,
        3: null,
        4: null
    });
    const [images, setImages] = useState([]);

    function nextSlide() {
        setSlide(slide + 1);
        contentRef.current.scrollTop = 0;
    }

    function previousSlide() {
        setSlide(slide - 1);
        contentRef.current.scrollTop = 0;
    }

    async function handleImageSubmit(event) {
        event.preventDefault();
        // validate images (not empty, less than 4, each less than 5mb)
        if (images.length === 0) {
            handleError("Please upload an image")
            return;
        }
        let imageData = [];
        for (let i = 0; i < images.length; i++) {
            const imageId = uuidv4();
            const imageRef = ref(storage, `images/${imageId} - ${images[i].name}`);
            try {
                const snapshot = await uploadBytes(imageRef, images[i]);
                const url = await getDownloadURL(snapshot.ref);
                imageData.push({url: url, id: imageId});
            } catch (error) {
                handleError("An unknown error occured")
            }
        }
        
        setImages([]);
        if (imageData.length === 0) {
            return;
        }
        fetch(serverURL + "/upload-work",
            {
                credentials: 'include', 
                method: "POST", 
                headers: {
                    "Content-Type": "application/json", 'Authorization': `Bearer ${localStorage.getItem('token')}`
                }, 
                body: JSON.stringify({ 
                    careerName: "UI/UX Designer",
                    imageData: imageData, 
                    exerciseName: "design website", 
                })
            })
            .then(async response => {
                const { error, message } = await response.json();
                if (error) {
                    handleError(message)
                }
                handleNotification("Nice work! Your work has been submitted!")
                setIsLoading(false);

            })
            .catch(async err => {
                handleError("An unknown error occured")
                setIsLoading(false);
            });
        

      };


    function onSubmitResponse(id, e) {
        e.preventDefault();
        // if no website idea (question 1), show error
        if (websiteIdea === "") {
            handleError("Please enter a website idea on the second page");
            return;
        }
        if (process.env.REACT_APP_ENVIRONMENT === "production") {
            logEvent(analytics, 'career_interaction')
        }
        // Get student response
        const answer = id === 1 ? websiteIdea : e.target.querySelector('input').value;
        // If no response or response is being graded, do nothing
        if (answer === "" || isLoading) {
            return;
        }
        setIsLoading(true);
        // Show loader to indicate response is being graded
        setFrqResults({ ...frqResults, [id]: "..." })
        // Clear input box
        if (id !== 1) {
            e.target.querySelector('input').value = "";
        }

        fetch(serverURL + "/uiux-designer-frq",
            {
                credentials: 'include', method: "POST", headers: {
                    "Content-Type": "application/json", 'Authorization': `Bearer ${localStorage.getItem('token')}`
                }, body: JSON.stringify({ message: answer, problemNumber: id, websiteIdea: websiteIdea })
            })
            .then(async response => {
                const { error, message } = await response.json();
                if (error) {
                    handleError(message)
                } else {
                    if (id === 1) {
                        setSampyThoughts(message);
                    }
                    setFrqResults({ ...frqResults, [id]: message })
                }
                setIsLoading(false);

            })
            .catch(async err => {
                handleError("An unknown error occured")
                setIsLoading(false);
            });
    }

    return (
        <>
            {window.innerWidth < 750 ? <ScreenWarning /> : 
                <>
                    <div className='uiux-designer content-box' ref={contentRef}>
                        <div className='slide' style={{display: slide===1 ? "block" : "none"}}>
                            <h1>What is UI/UX design?</h1>
                            <p>UI (user interface) and UX (user experience) design are two closely related disciplines in the field of design. UI design refers to the visual elements and layout of a digital product which a user interacts with, like designing buttons, typography, and overall style of the interface. UX design focuses on the experience - how easy is it to interact with the product, if a product is meeting the users goals. Together, these two disciplines aim to create an easy and pleasant experience for digital users.</p>
                            <p>Though there is widespread debate on whether these two disciplines should be seperated or not, in this page you'll have the opportunity to experience both. You'll design your very own website by understanding your user, defining goals and features, building a layout, and adding visual design.</p>
                            <div style={{textAlign: "center"}}>
                                <img src={require("../../img/design-trends.png")} alt="evolution of design trends" style={{width: "900px", maxWidth: "90%"}}/>
                                <p>The evolution of design trends in UI/UX. Digital design has progressed from skeuomorphism (characterized by the imitation of physical objects and real textures), to flat design (characterized by minimalism and simplicity) and neumorphism (characterized by softness and visual depth). Neumorphism has gained attention, but is not as popular as the other two.</p>
                            </div>
                        </div>

                        <div className='slide' style={{display: slide===2 ? "block" : "none"}}>
                            <h1>Understanding the User</h1>
                            <p>In this page, we will work to design the website of your dreams! Let's start of by seeing what you want to build.</p>
                            <Dropdown 
                                parent={<p className='hint'>Stuck? Click here for some ideas</p>} 
                                child={
                                <ul>
                                    <li>A website that takes in ingredients you have in your fridge and suggests dishes with recipes</li>
                                    <li>A website that allows you to create a to-do list and set reminders</li>
                                    <li>A blog website about travel</li>
                                    <li>A website that suggests books that allows people in the book community to interact</li>
                                </ul>
                                }/>
                            <p>Enter your website idea below:</p>
                            <div className='frq row' style={{gap: "5px"}}>
                                <p>I'm building a website that </p><input type="text" value={websiteIdea} onChange={(e)=>setWebsiteIdea(e.target.value)} placeholder="helps people explore careers through interactive exercises" />
                            </div>

                            <p>Great! Before we start designing, we need to know what we need to build! To do this, a clear understanding of our users is crucial. Since we are building a website for our users, it’s only right to think from their perspective, what features and elements we need before we start building.</p>
                            <p>Find a friend or family member and talk about your website idea with them. Get some general opinions on what they think of it. If you prefer, Sampy can tell you what it thinks of your website:</p>
                            <div className='center'>
                                <button className="primary-btn" onClick={(e) => onSubmitResponse(1, e)}>Sampy, what do you think?</button>
                            </div>
                            {frqResults[1] !== null &&
                                    <div className="chat-message row assistant" style={{ marginTop: "15px", justifyContent: "space-between", alignItems: "flex-start" }}>
                                        <div>
                                            <img alt="chatbot" src={require("../../img/chatbot.png")} height="30px" />
                                        </div>
                                        <div className='message-content'>{frqResults[1]}</div>
                                    </div>
                            }
                            
                            <h2>User Persona</h2>
                            <p>Now, let's create a user persona for your website. A user persona is just a hypothetical person that represents your core users - What will a person using your app look like?  Usually, it’s best to research many potential users of your website, but for now, let’s build a user persona from your vision and conversation with friends. This helps you understand and narrow down your audience while reminding you who you're building the website for. Here's an example:</p>
                            <div className='center'>
                                <img src={require("../../img/user-persona.png")} alt="user persona" style={{width: "900px", maxWidth: "90%"}}/>
                            </div>
                            <p>Your turn! Create a user persona for your website. It doesn't need to be too specific, but should provide a general idea of your target users. </p>
                            <form className='frq row' onSubmit={(e)=>onSubmitResponse(2, e)}>
                                <input type="text" placeholder="My user persona is ..." />
                                <button className="primary-btn" >Submit</button>
                            </form>
                            {frqResults[2] !== null &&
                                <div className="chat-message row assistant" style={{ marginTop: "15px", justifyContent: "space-between", alignItems: "flex-start" }} >
                                    <div>
                                        <img alt="chatbot" src={require("../../img/chatbot.png")} height="30px" />
                                    </div>
                                    <div className='message-content'>{frqResults[2]}</div>
                                </div>
                            }
                        </div>

                        <div className='slide' style={{display: slide===3 ? "block" : "none"}}>
                            <h1>Defining Goals and Features</h1>
                            <p>Now we can begin drafting the details of your website. How annoying would it be if you were halfway into building your website, and realized you forgot to account for a critical website feature? That’s why it’s important to clearly define what you will build, before designing it. Let’s focus on a few core features that the user will see, you can always brainstorm or add minor details later. Here are some examples:</p>
                            <div className='col' style={{gap: "10px"}}>
                                <Dropdown
                                    parent={<button className='primary-btn blue-bg'>Ecommerce Website&nbsp;&nbsp;<i className="fa fa-chevron-down" aria-hidden="true"></i></button>}
                                    child={<ul>
                                        <li>Search bar</li>
                                        <li>Product search and filtering</li>
                                        <li>Shopping cart</li>
                                        <li>Payment page</li>
                                        <li>Product review/ratings</li>
                                    </ul>}
                                />
                                <Dropdown
                                    parent={<button className='primary-btn blue-bg'>Fitness Website&nbsp;&nbsp;<i className="fa fa-chevron-down" aria-hidden="true"></i></button>}
                                    child={<ul>
                                        <li>User Profiles - age, weight, level, goals</li>
                                        <li>Analytic dashboard - track physical metrics, like workout consistency, heart rate, weight progression</li>
                                        <li>GPS tracking - track outdoor activities like running, biking</li>
                                        <li>Social sharing - be able to share progress online</li>
                                    </ul>}
                                />
                                <Dropdown
                                    parent={<button className='primary-btn blue-bg'>Task Management Website&nbsp;&nbsp;<i className="fa fa-chevron-down" aria-hidden="true"></i></button>}
                                    child={<ul>
                                        <li>Task manager - show current tasks, and be able to add or delete tasks</li>
                                        <li>Sharing - be able to share a taks or list of tasks with others</li>
                                        <li>Calendar/alarm integration - trigger task reminders at chosen time</li>
                                    </ul>}
                                />
                            </div>
                            

                            <p>Think back on what your family/friends said about your website and your vision of the website.</p>
                            {sampyThoughts && 
                                <div>
                                    <p>Since you asked Sampy, here are Sampy's thoughts again:</p>
                                    <div className={"chat-message row assistant"} style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
                                        <div>
                                            <img alt="chatbot" src={require("../../img/chatbot.png")} height="30px" />
                                        </div>
                                        <div className='message-content'>{sampyThoughts}</div>
                                     </div>
                                </div>
                            }
                            <p> What features or requirements should the website have?</p>
                            <form className='frq row' onSubmit={(e)=>onSubmitResponse(3, e)}>
                                <input type="text" placeholder="The website should ..." />
                                <button className="primary-btn" >Submit</button>
                            </form>
                            {frqResults[3] !== null &&
                                <div className="chat-message row assistant" style={{ marginTop: "15px", justifyContent: "space-between", alignItems: "flex-start" }} >
                                    <div>
                                        <img alt="chatbot" src={require("../../img/chatbot.png")} height="30px" />
                                    </div>
                                    <div className='message-content'>{frqResults[3]}</div>
                                </div>
                            }
                        </div>

                        <div className='slide' style={{display: slide===4 ? "block" : "none"}}>
                            <h1>Creating a Layout</h1>
                            <p>Now let's dive into drafting the layout of the website! Before we start, you should learn to use Figma. It's a versatile design tool widely used across the design industry. Plus, it's free and easy to use as well! Here's a quick tutorial on Figma and how to use it alongside the following exercises!</p>

                            <h2>User Flow</h2>
                            <p>Now, let's create a user flow for your website. This just means the steps the user will take to achieve some goal in your app, or navigate within the interface.  This also gives us an idea of the different screens and pages we’ll need. Here’s an example for a transport app like Uber (colors represent actions that might be on the same screen):</p>
                            <ul>
                                <li>Open app</li>
                                <li className='light-blue-bg'>Login or register</li>
                                <li className='light-green-bg'>Setup a pickup location</li>
                                <li className='light-green-bg'>Choose destination</li>
                                <li className='light-green-bg'>View and select ride options</li>
                                <li className='light-green-bg'>Get matched with a driver, and view pickup spot</li>
                                <li>Driver arrives and takes you to destination</li>
                                <li>Get off</li>
                                <li className='light-red-bg'>Get charged</li>
                                <li className='light-red-bg'> Leave a rating</li>
                            </ul>
                            <p>Now, create a user flow for your website, and describe it:</p>
                            <form className='frq row' onSubmit={(e) => onSubmitResponse(4, e)}>
                                <input type="text" placeholder="Type your user flow here" />
                                <button className="primary-btn" >Submit</button>
                            </form>
                            {frqResults[4] !== null &&
                                <div className="chat-message row assistant" style={{ marginTop: "15px", justifyContent: "space-between", alignItems: "flex-start" }} >
                                    <div>
                                        <img alt="chatbot" src={require("../../img/chatbot.png")} height="30px" />
                                    </div>
                                    <div className='message-content'>{frqResults[4]}</div>
                                </div>
                            }

                            <h2>Figma</h2>
                            <p>Now we can begin designing! We will be using figma, a tool that is commonly used within the design community. We've created a figma document where you can follow along the exercises.</p>
                            <div className='center' style={{marginBottom: "20px"}}>
                                <button className='primary-btn' onClick={
                                    ()=> window.open(
                                        "https://www.figma.com/file/R3YeEYpeUXsFWQl3NnAGY6/UI%2FUX-Design?type=design&node-id=10-34&mode=design&t=1A3b5CrNpDfcCRJr-0", "_blank")
                                    }>Go To Figma Doc</button>
                            </div>
                            <div className='center'>
                                <iframe width="560" height="315" src="https://www.youtube.com/embed/XopTtql8HX4?si=DP7K7hRJIzAAJgfS" title="YouTube video player"  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen="allowFullScreen"></iframe>
                            </div>
                            


                            <h2>Wireframes</h2>
                            <p>Now, you’ll create some wireframes, which is the skeleton of your app. With wireframing, you’ll brainstorm the layout of your elements on different screens. You’ll use basic shapes, placeholders, and text to represent different elements.</p>
                            <p>Head over to the "Wireframeing" page of the figma document, and create your wireframe using your user flow.</p>
                        </div>

                        <div className='slide' style={{display: slide===5 ? "block" : "none"}}>
                            <h1>Visual Design Principles</h1>
                            <p>Finally, we're ready to design and style the visual interface. Before we start, let's cover some basic best practices and guidelines.</p>
                            <h2>Typography</h2>
                            <ul>
                                <li>Make sure to establish heirarchy. Titles should look different than headers which should look different than paragraph text. You can differentiate using font size, font family, and/or font weight (how bold the font is). Not all text is created equal.</li>
                            </ul>
                            <h2>Contrast</h2>
                            <ul>
                                <li>Text or elements on your page should have good contrast with the background - your website should be easy to read</li>
                            </ul>
                            <div className='center'>
                                <img src={require("../../img/contrast.jpg")} alt="contrast" style={{width: "800px", maxWidth: "90%"}}/>
                            </div>
                            <h2>Color</h2>
                            <ul>
                                <li>Colors give off different moods, try to pick one that is appropriate with your website. </li>
                                <li>Color harmony is also important. Mixing and matching without much thought probably won't go well. We recommend <a href="https://coolors.co/" target="_blank" rel="noopener noreferrer">Coolors</a>, a free online tool to generate beautiful color palettes.</li>
                            </ul>
                            <h2>Consistency</h2>
                            <ul>
                                <li>Be consistent. If a paragraph is in Times New Roman, don't make the next one in Comic Sans. If a button is rounded, other same level buttons should be rounded too. This helps your users recognize your brand - just like how blue and white makes you think of Twitter/X.</li>
                            </ul>
                            <p>Let's test what you know! Here's a game to test your user interface design judgement.</p>
                            <SpotTheDifference />

                        </div>

                        <div className='slide' style={{display: slide===6 ? "block" : "none"}}>
                            <h1>Design Your Website</h1>
                            
                            <p>Now you can work towards completing your website design! Head back to the figma file to the "Final Design" section and fully build out your user interface! Don't hesitate to look online for inspiration. When you're finished, export your designs and submit them here!</p>
                            
                            <div className='center' >
                                <iframe width="560" height="315" src="https://www.youtube.com/embed/5vvywB7wcxM?si=qyvQdg-RhjPKxkZT" title="YouTube video player"  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen="allowFullScreen"></iframe>
                                <p>Here's how to export your work from Figma.</p>
                            </div>


                            <div className='center col' style={{gap: "5px", width: "fit-content", margin: "auto"}}>
                                <label className='file-input'>
                                    <input type="file" accept="image/*" multiple onChange={(e) => setImages(e.target.files)} />
                                    Upload Images
                                </label>
                                <div className='selected-files' style={{textAlign: "left"}}>
                                    {Array.from(images).map((image, index) => {
                                        return <p key={index}>{image.name}</p>
                                    })}
                                </div>
                                <button className="primary-btn" onClick={handleImageSubmit}>Submit</button>
                            </div>
                        </div>

                        <div className='slide' style={{display: slide===7 ? "block" : "none"}}>
                            <h1>Recommended Resources</h1>
                            <p>Here are some resources to help you get started with UI/UX design!</p>
                            <h3>Blogs/Websites</h3>
                            <p><a href="https://lawsofux.com/" target='_blank' rel="noreferrer">Laws of UX</a> - A collection of best practices that designers can consider when building user interfaces.</p>
                            <p><a href="https://uxplanet.org/how-to-become-a-ux-designer-at-40-with-no-previous-digital-or-design-experience-5c96af46b73c" target='_blank' rel="noreferrer">How to Become A UX Designer at 40 With No Previous Digital or Design Experience</a> - Everything from studying UX Design, to tools to learn, to portfolios, to getting a job.</p>
                            <p><a href="https://www.nngroup.com/articles/" target='_blank' rel="noreferrer">Nielsen Norman Group</a> - Learn UI/UX from the pioneers of user experience. They also have a really popular article called "10 Usability Heuristics for User Interface Design," be sure to check it out!</p>
                            <p><a href="https://uxplanet.org/100-ui-resources-tools-books-all-you-need-for-your-ui-work-ad959719fdca" target='_blank' rel="noreferrer">100+ UI Resources, Tools & Books: All You Need For Your UI Work</a> - A collection of tools for colors, typography, inspiration, stock photos/videos and more that can be quickly applied in your next project.</p>
                            
                            <h3>Courses</h3>
                            <p><a href="https://www.coursera.org/specializations/michiganux?irclickid=X0gWovwlXxyPRD6Q14QVLzSnUkFyUGytrxgq1s0&irgwc=1&utm_medium=partners&utm_source=impact&utm_campaign=3294490&utm_content=b2c" target='_blank' rel="noreferrer">User Experience Research and Design</a> - A course tailored to students with no experience to learn UX.</p>
                            <p><a href="https://www.coursera.org/professional-certificates/google-ux-design?irclickid=X0gWovwlXxyPRD6Q14QVLzSnUkFyUD0hrxgq1s0&irgwc=1&utm_medium=partners&utm_source=impact&utm_campaign=3294490&utm_content=b2c" target='_blank' rel="noreferrer">Google UX Design Professional Certificate</a> - A comprehensive UI/UX course by Google. This course is great because it's hands-on (you'll build 3 projects that you can add to your portfolio) and gives you a certificate from Google.</p>


                            <h1>Project Ideas</h1>
                            <h3>UI Redesign</h3>
                            <p>Find a piece of software that you commonly use and look for some improvements. Redesign it to make it more user-friendly and appealing. Bonus if you contact the company and should them your work!</p>
                            <h3>Design Some Software</h3>
                            <p>Think of a problem in your life that others may share, and follow the UX/UI steps to design an app that would solve this problem. If you're not sure and want to first develop your skills, here are some website/app ideas that often end up cluttered and complex, and require thoughtful design:
                            </p>
                            <ul>
                                <li>Travel Booking Platform</li>
                                <li>Health and Fitness Platform</li>
                                <li>Food Delivery Platform</li>
                                <li>Education Platform</li>
                                <li>Music or Video Streaming Platform</li>
                            </ul>
                            <p>Still, we recommend you design an idea of your own. It makes it a lot more fun and rewarding!</p>
                            <h3>Use Your Powers For Good</h3>
                            <p>Once you have a small portfolio, volunteer your skills! You can contact a small business and offer to draft some designs for them, or volunteer for <a href="https://www.google.com/search?q=civic+technology+volunteer&sca_esv=560574176&ei=VBDsZOnVOOC1qtsPv_e5sAc&ved=0ahUKEwjpz8_hsP6AAxXgmmoFHb97DnYQ4dUDCBA&uact=5&oq=civic+technology+volunteer&gs_lp=Egxnd3Mtd2l6LXNlcnAiGmNpdmljIHRlY2hub2xvZ3kgdm9sdW50ZWVyMgUQIRigATIFECEYoAEyBRAhGKABMgUQIRigATIFECEYqwJIvg5Q-ARY3g1wAngAkAEAmAF-oAGvCKoBAzAuObgBA8gBAPgBAcICChAAGEcY1gQYsAPCAgYQABgWGB7CAggQABgWGB4YCsICChAAGBYYHhgPGArCAggQABiKBRiGA-IDBBgAIEGIBgGQBgg&sclient=gws-wiz-serp" target='_blank' rel="noreferrer">civic technology</a> or social good projects.</p>

                            <Feedback />
                        </div>

                    </div>

                    <div className="navigation-wrapper">
                        <div className="navigation row">
                            {slide !== 1 && <button className="secondary-btn" onClick={previousSlide}>Back</button>}
                            {slide !== 7 && <button className="primary-btn" onClick={nextSlide}>Next</button>}
                            {slide === 7 && <button className="primary-btn" onClick={() => window.location.href="/careers"}>Return Home</button>}
                        </div>
                    </div>
                </>
            }
        </>
        
    )
}
